import { useEffect, useRef, useState } from 'react';
import { useScenarioStore } from '../store/useScenarioStore';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveState {
  status: AutosaveStatus;
  lastError: string | null;
  retry: () => void;
}

interface AutosaveOptions {
  enabled: boolean;       // turn off when not in editor
  debounceMs?: number;    // default 1500
  // Whether the current scenario is "ready" to autosave. New scenarios should
  // not create empty placeholder rows in the DB; require both a non-empty name
  // and at least one line item / vehicle.
  readyToSave: boolean;
}

/**
 * Phase 22.1 — debounced autosave for the current scenario.
 *
 * Triggers a save 1500ms after the last change to currentScenario. New
 * scenarios (currentScenarioId === null) only save once readyToSave is true,
 * preventing empty placeholders. If a save fails, status flips to "error"
 * and the caller can show a retry button. Calling retry() force-saves the
 * latest snapshot.
 */
export function useScenarioAutosave({
  enabled,
  debounceMs = 1500,
  readyToSave,
}: AutosaveOptions): AutosaveState {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastError, setLastError] = useState<string | null>(null);

  // Subscribe selectively so we re-run only when scenario content actually changes.
  const currentScenario = useScenarioStore((s) => s.currentScenario);
  const currentScenarioId = useScenarioStore((s) => s.currentScenarioId);
  const saveScenario = useScenarioStore((s) => s.saveScenario);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const dirtySinceLastSaveRef = useRef(false);
  const lastSerializedRef = useRef<string | null>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist the saveScenario reference so retry() always calls the latest action.
  const saveRef = useRef(saveScenario);
  saveRef.current = saveScenario;

  async function flushSave() {
    if (!enabled || !readyToSave) return;
    if (inFlightRef.current) {
      // Already saving — mark dirty so we re-flush after.
      dirtySinceLastSaveRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setStatus('saving');
    setLastError(null);
    try {
      await saveRef.current();
      lastSerializedRef.current = JSON.stringify(useScenarioStore.getState().currentScenario);
      setStatus('saved');
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = setTimeout(() => {
        setStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 2500);
    } catch (e) {
      setLastError((e as Error).message ?? 'Autosave failed');
      setStatus('error');
    } finally {
      inFlightRef.current = false;
      if (dirtySinceLastSaveRef.current) {
        dirtySinceLastSaveRef.current = false;
        // Re-flush queued changes.
        flushSave();
      }
    }
  }

  // Debounced trigger on every meaningful scenario change.
  useEffect(() => {
    if (!enabled) return;
    const serialized = JSON.stringify(currentScenario);
    if (lastSerializedRef.current === null) {
      // First mount — record baseline without triggering save.
      lastSerializedRef.current = serialized;
      return;
    }
    if (serialized === lastSerializedRef.current) return;
    if (!readyToSave) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      flushSave();
    }, debounceMs);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScenario, readyToSave, enabled, debounceMs, currentScenarioId]);

  // Reset baseline when switching to a different scenario id.
  useEffect(() => {
    lastSerializedRef.current = JSON.stringify(useScenarioStore.getState().currentScenario);
    setStatus('idle');
    setLastError(null);
  }, [currentScenarioId]);

  return {
    status,
    lastError,
    retry: flushSave,
  };
}
